import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Heart, Trophy, Share2, Copy, Instagram, Music2, Clock, Users, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

const VIOLET = "#5B4FE0";
const GOLD = "#C8A832";
const DARK = "#0a0a0a";
const POLL_MS = 5000;
const VOTER_KEY = "launchpad:the-cut:voter";
const VOTED_KEY = "launchpad:the-cut:voted";

function getVoterToken() {
  let t = null;
  try { t = localStorage.getItem(VOTER_KEY); } catch { /* ignore */ }
  if (!t) {
    t = "v_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    try { localStorage.setItem(VOTER_KEY, t); } catch { /* ignore */ }
  }
  return t;
}
function loadVoted() {
  try { return JSON.parse(localStorage.getItem(VOTED_KEY) || "{}"); } catch { return {}; }
}
function saveVoted(map) {
  try { localStorage.setItem(VOTED_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

export default function CutBrand() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState(() => loadVoted());
  const [busy, setBusy] = useState({});
  const voterToken = useMemo(getVoterToken, []);

  const refresh = useCallback(async () => {
    try {
      const d = await api.cutGetBrand(slug);
      setData(d);
      setLoading(false); // render as soon as brand data is in; leaderboard fills in after
      if (d?.round?.id) {
        const lb = await api.cutGetLeaderboard(d.round.id);
        setLeaderboard(lb.brands || []);
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll every 5s while the tab is visible
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const handleVote = async (productId) => {
    if (busy[productId]) return;
    setBusy((b) => ({ ...b, [productId]: true }));
    // Optimistic UI
    const wasVoted = !!voted[productId];
    const nextVoted = { ...voted, [productId]: !wasVoted };
    setVoted(nextVoted); saveVoted(nextVoted);
    setData((prev) => {
      if (!prev) return prev;
      const products = prev.products.map((p) =>
        p.id === productId ? { ...p, votes: Math.max(0, (p.votes || 0) + (wasVoted ? -1 : 1)) } : p
      );
      const totalVotes = products.reduce((s, p) => s + (p.votes || 0), 0);
      products.sort((a, b) => (b.votes - a.votes) || (a.displayOrder - b.displayOrder));
      products.forEach((p, i) => { p.rank = i + 1; });
      return { ...prev, products, brand: { ...prev.brand, totalVotes } };
    });
    try {
      await api.cutVote(productId, voterToken);
      // Immediately refresh leaderboard so strip reflects new rank
      if (data?.round?.id) {
        api.cutGetLeaderboard(data.round.id).then((lb) => setLeaderboard(lb.brands || []));
      }
    } catch (e) {
      // rollback
      setVoted(voted); saveVoted(voted);
      toast.error(e?.response?.data?.detail || "Could not record vote");
      refresh();
    } finally {
      setBusy((b) => ({ ...b, [productId]: false }));
    }
  };

  if (loading) return <FullscreenNote>Loading The Cut…</FullscreenNote>;
  if (!data) return <FullscreenNote>Brand not found — check the URL.</FullscreenNote>;

  const { brand, products, round } = data;
  const myRank = leaderboard.find((b) => b.id === brand.id)?.rank || brand.rank || "—";

  return (
    <div style={{ background: DARK, color: "#fff", minHeight: "100vh", fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }} data-testid="cut-brand-page">
      <style>{PAGE_CSS}</style>
      <StickyHeader brand={brand} total={brand.totalVotes} />
      {brand.isWinner && <WinnerRibbon brand={brand} round={round} />}
      <BrandHero brand={brand} productCount={products.length} rank={myRank} leaderboard={leaderboard} total={brand.totalVotes} round={round} />
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 24px" }}>
        <ProductGrid products={products} voted={voted} busy={busy} onVote={handleVote} />
        <LeaderboardStrip brands={leaderboard} highlightId={brand.id} />
        <ShareRow brand={brand} />
        <EmailCapture voterToken={voterToken} brandName={brand.brandName} />
        <div style={{ height: 60 }} />
      </div>
    </div>
  );
}

/* ==================== Winner ribbon ==================== */
function WinnerRibbon({ brand, round }) {
  return (
    <div className="cut-winner-ribbon" data-testid="cut-winner-ribbon">
      <div className="cut-winner-stripes" />
      <div className="cut-winner-inner">
        <div className="cut-winner-trophy" aria-hidden>🏆</div>
        <div className="cut-winner-copy">
          <div className="cut-winner-eyebrow">WINNER · {round?.name || "THE CUT"}</div>
          <div className="cut-winner-title">{brand.brandName} won The Cut</div>
          <div className="cut-winner-sub">The community voted them in — pre-order is now open below.</div>
        </div>
      </div>
    </div>
  );
}

/* ==================== Sticky header ==================== */
function StickyHeader({ brand, total }) {
  const initials = (brand.brandName || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <header className="cut-sticky" data-testid="cut-sticky-header">
      <Link to="/storefront" className="cut-sticky-back" data-testid="cut-back-link">
        <ArrowLeft size={14} />
      </Link>
      <div className="cut-sticky-avatar">{initials}</div>
      <div className="cut-sticky-name">{brand.brandName}</div>
      <div className="cut-sticky-live">
        <span className="cut-live-dot" />
        LIVE
      </div>
      <AnimatedNumber value={total} className="cut-sticky-total" data-testid="cut-sticky-total" />
    </header>
  );
}

/* ==================== Animated gold number ==================== */
function AnimatedNumber({ value, className, raw, ...rest }) {
  const [displayed, setDisplayed] = useState(value);
  const [pulse, setPulse] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (value === prev.current) return;
    setPulse(true);
    setDisplayed(value);
    const t = setTimeout(() => setPulse(false), 350);
    prev.current = value;
    return () => clearTimeout(t);
  }, [value]);
  return (
    <span className={`${className || ""}${pulse ? " cut-number-pulse" : ""}`} {...rest}>
      {raw ? (displayed || 0) : (displayed || 0).toLocaleString()}
    </span>
  );
}

/* ==================== Brand hero ==================== */
function BrandHero({ brand, productCount, rank, leaderboard, total, round }) {
  return (
    <section className="cut-hero" data-testid="cut-brand-hero">
      <div className="cut-hero-inner cut-hero-centered">
        <div className="cut-eyebrow" style={{ color: GOLD }}><Sparkles size={12} /> THE CUT · BRAND ENTRY</div>
        <h1 className="cut-hero-title">{brand.brandName}</h1>
        <p className="cut-hero-tagline">{brand.tagline}</p>

        <VoteHero brandName={brand.brandName} brandId={brand.id} total={total} rank={rank} leaderboard={leaderboard} />
        <VoteTimer endDate={round?.endDate} closed={round?.status !== "active"} />
      </div>
    </section>
  );
}
const Stat = ({ label, value, sub }) => (
  <div className="cut-stat">
    <div className="cut-stat-value">{value}</div>
    <div className="cut-stat-label">{label}</div>
    {sub && <div className="cut-stat-sub">{sub}</div>}
  </div>
);

/* ==================== Vote hero (top counter) ==================== */
function VoteHero({ brandName, brandId, total, rank, leaderboard }) {
  const leader = leaderboard && leaderboard[0];
  const isLeader = leader && leader.id === brandId;
  let pct = 0;
  if (leader) {
    if (isLeader) {
      pct = 100;
    } else {
      pct = leader.totalVotes > 0 ? Math.min(100, Math.round((total / leader.totalVotes) * 100)) : 0;
    }
  }
  return (
    <div className="cut-votehero" data-testid="cut-vote-hero">
      <div className="cut-votehero-label">VOTES FOR {(brandName || "").toUpperCase()}</div>
      <div className="cut-votehero-row">
        <AnimatedNumber value={total} raw className="cut-votehero-num" data-testid="cut-vote-hero-num" />
        <span className="cut-votehero-rank" data-testid="cut-vote-hero-rank">#{rank} in The Cut</span>
      </div>
      <div className="cut-votehero-bar"><div className="cut-votehero-bar-fill" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

/* ==================== Inline timer line ==================== */
function VoteTimer({ endDate, closed }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  let text = "Voting closed";
  if (!closed && endDate) {
    const ms = new Date(endDate).getTime() - now;
    if (ms > 0) {
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      let units;
      if (d > 0) units = `${pad(d)}d ${pad(h)}h ${pad(m)}m`;
      else if (h > 0) units = `${pad(h)}h ${pad(m)}m`;
      else units = `00h ${pad(m)}m ${pad(s)}s`;
      text = `⏱ ${units} left to vote`;
    }
  }
  return <div className="cut-timer-line" data-testid="cut-timer-line">{text}</div>;
}

/* ==================== Product grid ==================== */
function ProductGrid({ products, voted, busy, onVote }) {
  const [visible, setVisible] = useState(24);
  const shown = products.slice(0, visible);
  const remaining = products.length - shown.length;
  return (
    <section className="cut-section" data-testid="cut-product-grid">
      <div className="cut-eyebrow" style={{ marginBottom: 18 }}>THE PRODUCTS · {products.length}</div>
      <div className="cut-grid">
        {shown.map((p) => (
          <ProductCard key={p.id} p={p} voted={!!voted[p.id]} busy={!!busy[p.id]} onVote={() => onVote(p.id)} />
        ))}
      </div>
      {remaining > 0 && (
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <button type="button" onClick={() => setVisible((v) => v + 24)} data-testid="cut-load-more-btn"
            style={{ background: "transparent", border: `1px solid ${VIOLET}`, color: VIOLET, padding: "13px 28px", borderRadius: 10, fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer" }}>
            Load more ({remaining} left)
          </button>
        </div>
      )}
    </section>
  );
}
function ProductCard({ p, voted, busy, onVote }) {
  const max = 100; // visual max per product bar
  const pct = Math.min(100, Math.round((p.votes / max) * 100));
  return (
    <div className="cut-card" data-testid={`cut-product-card-${p.id}`}>
      <div className="cut-card-media">
        {p.imageUrl ? (
          <img src={p.imageUrl} alt={p.name} loading="lazy" />
        ) : (
          <DStripe />
        )}
        <div className="cut-card-rank">#{p.rank}</div>
      </div>
      <div className="cut-card-body">
        <div className="cut-card-cat">{p.category}</div>
        <div className="cut-card-name">{p.name}</div>
        <div className="cut-card-price">${Number(p.price).toFixed(0)}</div>
        <div className="cut-card-bar-row">
          <div className="cut-card-bar"><div className="cut-card-bar-fill" style={{ width: `${pct}%` }} /></div>
          <AnimatedNumber value={p.votes || 0} className="cut-card-votes" data-testid={`cut-product-votes-${p.id}`} />
        </div>
        <button type="button" onClick={onVote} disabled={busy}
          className={`cut-vote-btn${voted ? " voted" : ""}`}
          data-testid={`cut-vote-btn-${p.id}`}
          aria-pressed={voted}>
          <Heart size={13} fill={voted ? "#0a0a0a" : "none"} />
          {voted ? "Voted" : "Vote"}
        </button>
      </div>
    </div>
  );
}

/* Deluxxie D stripe pattern placeholder */
const DStripe = () => (
  <svg viewBox="0 0 200 260" preserveAspectRatio="xMidYMid slice" style={{ width: "100%", height: "100%", background: "#141414" }}>
    {[220, 200, 178, 152, 122, 88, 56, 32].map((h, i) => (
      <rect key={i} x={16 + i * 22} y={(260 - h) / 2} width="14" height={h} fill={i === 0 ? VIOLET : "#2a2a2a"} rx="1" />
    ))}
  </svg>
);

/* ==================== Leaderboard strip ==================== */
function LeaderboardStrip({ brands, highlightId }) {
  if (!brands || brands.length === 0) return null;
  const top = brands.slice(0, 5);
  const max = Math.max(1, top[0].totalVotes);
  return (
    <section className="cut-section" data-testid="cut-leaderboard-strip">
      <div className="cut-eyebrow" style={{ marginBottom: 14 }}><Trophy size={12} /> THE CUT LEADERBOARD</div>
      <div className="cut-lb">
        {top.map((b) => {
          const pct = Math.max(3, Math.round((b.totalVotes / max) * 100));
          const isMe = b.id === highlightId;
          return (
            <div key={b.id} className={`cut-lb-row${isMe ? " me" : ""}`} data-testid={`cut-lb-row-${b.id}`}>
              <div className="cut-lb-rank">#{b.rank}</div>
              <div className="cut-lb-name">{b.brandName}{isMe && <span className="cut-lb-you"> — you</span>}</div>
              <div className="cut-lb-bar"><div className="cut-lb-bar-fill" style={{ width: `${pct}%` }} /></div>
              <AnimatedNumber value={b.totalVotes} className="cut-lb-total" />
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ==================== Share row ==================== */
function safeCopy(text) {
  const fallback = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy"); ta.remove();
    } catch (e) { /* ignore */ }
  };
  try {
    const p = navigator.clipboard && navigator.clipboard.writeText(text);
    if (p && typeof p.catch === "function") p.catch(fallback); else if (!p) fallback();
  } catch (e) { fallback(); }
}

function ShareRow({ brand }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const shareUrl = `${apiBase}/api/the-cut/share/${encodeURIComponent(brand.slug)}`;
  const storyImg = `${apiBase}/api/the-cut/brands/${encodeURIComponent(brand.slug)}/share.png?format=story`;
  const copy = () => { safeCopy(shareUrl); toast.success("Link copied"); };
  const shareStory = (appName, appUrl) => {
    safeCopy(shareUrl);
    fetch(storyImg).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `${brand.slug}-the-cut.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1500);
    }).catch(() => window.open(storyImg, "_blank"));
    toast.success(`Story image saved + link copied — post it on ${appName}`);
    window.open(appUrl, "_blank");
  };
  return (
    <section className="cut-section" data-testid="cut-share-row">
      <div className="cut-share-grid">
        <button type="button" onClick={copy} className="cut-share-btn" data-testid="cut-share-copy">
          <Copy size={14} /> Copy vote link
        </button>
        <button type="button" onClick={() => shareStory("Instagram", "https://www.instagram.com/")} className="cut-share-btn" data-testid="cut-share-instagram">
          <Instagram size={14} /> Share to Instagram
        </button>
        <button type="button" onClick={() => shareStory("TikTok", "https://www.tiktok.com/upload")} className="cut-share-btn" data-testid="cut-share-tiktok">
          <Music2 size={14} /> Share to TikTok
        </button>
      </div>
    </section>
  );
}

const FullscreenNote = ({ children }) => (
  <div style={{ background: DARK, color: "#888", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif" }}>{children}</div>
);

function EmailCapture({ voterToken, brandName }) {
  const [email, setEmail] = useState("");
  const [saved, setSaved] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Enter a valid email");
    try { await api.cutAttachVoterEmail(voterToken, email); setSaved(true); toast.success("You're on the list"); }
    catch { toast.error("Could not save"); }
  };
  return (
    <section className="cut-section" data-testid="cut-email-capture">
      <div style={{ background: "#141414", border: ".5px solid #232323", borderRadius: 14, padding: 24, textAlign: "center" }}>
        {saved ? (
          <div>
            <div className="cut-eyebrow" style={{ marginBottom: 10 }}>✓ You&apos;re in</div>
            <div style={{ fontSize: 14, color: "#a8a8a8" }}>We&apos;ll email you when the round closes if {brandName} wins.</div>
          </div>
        ) : (
          <>
            <div className="cut-eyebrow" style={{ marginBottom: 10 }}>GET THE WIN ANNOUNCEMENT</div>
            <div style={{ fontSize: 14, color: "#a8a8a8", marginBottom: 16, maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>
              Drop your email — if {brandName} wins The Cut, we&apos;ll send you first access to pre-order.
            </div>
            <form onSubmit={submit} style={{ display: "flex", gap: 10, maxWidth: 460, margin: "0 auto", flexWrap: "wrap" }}>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                data-testid="cut-email-input"
                style={{ flex: 1, minWidth: 220, background: "#0a0a0a", border: ".5px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
              <button type="submit" data-testid="cut-email-submit"
                style={{ background: VIOLET, color: DARK, border: 0, padding: "12px 20px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: ".02em" }}>
                NOTIFY ME →
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

/* ==================== CSS ==================== */
const PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Anton&family=Orbitron:wght@500&family=Share+Tech+Mono&display=swap');
.cut-eyebrow{font-size:10px;font-weight:600;letter-spacing:0.12em;color:${VIOLET};display:inline-flex;align-items:center;gap:6px;text-transform:uppercase}
.cut-sticky{position:sticky;top:0;z-index:50;display:flex;align-items:center;gap:14px;padding:12px 20px;background:rgba(10,10,10,.92);backdrop-filter:blur(10px);border-bottom:1px solid #1a1a1a}
.cut-sticky-back{color:#888;text-decoration:none;padding:6px;border-radius:8px;display:inline-flex;align-items:center;transition:color .15s,background .15s}
.cut-sticky-back:hover{color:#fff;background:#1a1a1a}
.cut-sticky-avatar{width:34px;height:34px;border-radius:50%;background:${VIOLET};color:${DARK};display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;letter-spacing:.02em}
.cut-sticky-name{font-size:14px;font-weight:500;color:#fff;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cut-sticky-live{display:inline-flex;align-items:center;gap:6px;font-size:10px;font-weight:600;color:#888;letter-spacing:.14em}
.cut-live-dot{width:7px;height:7px;border-radius:50%;background:#5B4FE0;box-shadow:0 0 0 0 rgba(91,79,224,0.6);animation:cut-pulse 1.4s infinite}
@keyframes cut-pulse{0%{box-shadow:0 0 0 0 rgba(91,79,224,.55)}70%{box-shadow:0 0 0 8px rgba(91,79,224,0)}100%{box-shadow:0 0 0 0 rgba(91,79,224,0)}}
.cut-sticky-total{font-size:22px;font-weight:700;color:${VIOLET};font-variant-numeric:tabular-nums;transition:color .3s,transform .25s}
.cut-number-pulse{color:#fff !important;transform:scale(1.14)}

.cut-hero{padding:56px 24px 40px;background:linear-gradient(180deg,#0f0f0f 0%,#0a0a0a 100%);position:relative;overflow:hidden}
.cut-hero::after{content:"";position:absolute;right:-40px;top:-40px;width:280px;height:280px;background:radial-gradient(circle at center,rgba(91,79,224,.16) 0%,transparent 60%);pointer-events:none}
.cut-hero-inner{max-width:1120px;margin:0 auto;position:relative;z-index:1}
.cut-hero-title{font-family:'Anton',Impact,sans-serif;text-transform:uppercase;font-size:52px;font-weight:400;letter-spacing:.01em;margin:14px 0 6px;color:#fff;line-height:.95}
@media(min-width:768px){.cut-hero-title{font-size:76px}}
.cut-hero-tagline{font-size:15px;color:#a8a8a8;max-width:560px;line-height:1.55;margin-bottom:28px}
.cut-stats{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;max-width:640px;margin-bottom:26px}
@media(min-width:640px){.cut-stats{grid-template-columns:repeat(4,1fr)}}
.cut-stat{padding:14px 16px;background:#141414;border:.5px solid #232323;border-radius:12px}
.cut-stat-value{font-size:22px;font-weight:600;color:#fff;line-height:1.1;font-variant-numeric:tabular-nums}
.cut-stat-label{font-size:9px;letter-spacing:.12em;color:#666;text-transform:uppercase;font-weight:600;margin-top:6px}
.cut-stat-sub{font-size:10px;color:#888;margin-top:2px}
.cut-hero-story{max-width:680px;font-size:13px;line-height:1.75;color:#a8a8a8;padding:18px 22px;background:#141414;border-left:2px solid ${VIOLET};border-radius:0 12px 12px 0}

.cut-votehero{margin-top:26px;text-align:center}
.cut-votehero-label{font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#8A8A8A;font-weight:600}
.cut-votehero-row{display:flex;align-items:center;justify-content:center;gap:14px;margin-top:8px}
.cut-votehero-num{font-family:'Orbitron',sans-serif;font-weight:500;font-size:44px;line-height:1;color:#5B4FE0;font-variant-numeric:tabular-nums;transition:color .3s,transform .25s}
@media(min-width:768px){.cut-votehero-num{font-size:56px}}
.cut-votehero-rank{background:#1E1233;border:.5px solid #3A2A66;color:#B5AEF5;font-size:11px;font-weight:600;padding:5px 12px;border-radius:999px;letter-spacing:.02em;white-space:nowrap}
.cut-votehero-bar{width:170px;height:3px;background:#222222;border-radius:999px;overflow:hidden;margin:14px auto 0}
.cut-votehero-bar-fill{height:100%;background:#5B4FE0;transition:width .5s cubic-bezier(.2,.8,.2,1)}
.cut-votehero-caption{font-size:11px;color:#6F6F6F;margin-top:8px}
.cut-timer-line{font-family:'Share Tech Mono',monospace;font-size:12px;color:#8A8A8A;text-align:center;margin-top:14px}
.cut-stats-2{grid-template-columns:repeat(2,1fr) !important;max-width:440px;margin-top:28px}
.cut-hero-centered{text-align:center}
.cut-hero-centered .cut-eyebrow{justify-content:center}
.cut-hero-centered .cut-hero-tagline{margin-left:auto;margin-right:auto}
.cut-stats-1{grid-template-columns:minmax(160px,200px) !important;justify-content:center;max-width:none;margin-top:28px;margin-bottom:0;text-align:left}

.cut-section{margin-top:44px}
.cut-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:14px}
@media(min-width:640px){.cut-grid{grid-template-columns:repeat(3,1fr)}}
@media(min-width:1024px){.cut-grid{grid-template-columns:repeat(4,1fr)}}
.cut-card{background:#141414;border:.5px solid #232323;border-radius:14px;overflow:hidden;transition:border-color .2s,transform .25s cubic-bezier(.2,.8,.2,1),box-shadow .25s}
.cut-card:hover{border-color:#333;transform:translateY(-2px);box-shadow:0 12px 30px rgba(0,0,0,.35)}
.cut-card-media{position:relative;aspect-ratio:4/5;background:#0f0f0f;overflow:hidden}
.cut-card-media img{width:100%;height:100%;object-fit:cover;display:block}
.cut-card-rank{position:absolute;top:10px;left:10px;background:rgba(10,10,10,.85);color:${VIOLET};font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;border:.5px solid rgba(91,79,224,.35);letter-spacing:.04em}
.cut-card-body{padding:14px 16px 16px;display:flex;flex-direction:column;gap:6px}
.cut-card-cat{font-size:9px;letter-spacing:.14em;color:#666;text-transform:uppercase;font-weight:600}
.cut-card-name{font-size:14px;font-weight:500;color:#fff;line-height:1.3;min-height:36px;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
.cut-card-price{font-size:13px;color:${VIOLET};font-weight:600;letter-spacing:.02em}
.cut-card-bar-row{display:flex;align-items:center;gap:10px;margin-top:6px}
.cut-card-bar{flex:1;height:4px;background:#232323;border-radius:999px;overflow:hidden}
.cut-card-bar-fill{height:100%;background:${VIOLET};transition:width .5s cubic-bezier(.2,.8,.2,1)}
.cut-card-votes{font-size:12px;color:${VIOLET};font-weight:600;font-variant-numeric:tabular-nums;min-width:24px;text-align:right;transition:color .3s,transform .25s}
.cut-vote-btn{margin-top:8px;background:transparent;color:${VIOLET};border:1px solid ${VIOLET};border-radius:10px;padding:9px 10px;font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:6px;font-family:inherit;transition:background .18s,color .18s,transform .12s}
.cut-vote-btn:hover:not(:disabled){background:rgba(91,79,224,.14)}
.cut-vote-btn:active:not(:disabled){transform:scale(.98)}
.cut-vote-btn.voted{background:${VIOLET};color:${DARK};border-color:${VIOLET}}
.cut-vote-btn:disabled{opacity:.5;cursor:not-allowed}

.cut-total-card{margin-top:44px;padding:32px 28px;background:#141414;border:.5px solid #2a2a2a;border-radius:16px;display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap}
.cut-total-num{font-size:56px;font-weight:700;color:${VIOLET};line-height:1;font-variant-numeric:tabular-nums;display:block;margin-top:8px;transition:color .3s,transform .25s}
.cut-total-sub{font-size:11px;color:#666;margin-top:6px;letter-spacing:.02em}
.cut-total-right{display:flex;flex-direction:column;align-items:flex-end;gap:12px}
.cut-rank-badge{background:rgba(200,168,50,.14);color:${VIOLET};border:.5px solid rgba(200,168,50,.35);padding:6px 14px;border-radius:999px;font-size:11px;font-weight:600;letter-spacing:.04em}
.cut-share-inline{background:transparent;border:.5px solid #333;color:#a8a8a8;padding:8px 14px;border-radius:10px;font-size:11px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-family:inherit;transition:color .18s,border-color .18s}
.cut-share-inline:hover{color:#fff;border-color:#555}

.cut-lb{background:#141414;border:.5px solid #232323;border-radius:14px;padding:8px}
.cut-lb-row{display:grid;grid-template-columns:36px 1fr 2fr auto;align-items:center;gap:12px;padding:12px 14px;border-radius:10px;transition:background .18s}
.cut-lb-row:hover{background:#191919}
.cut-lb-row.me{background:rgba(91,79,224,.10);border:.5px solid rgba(91,79,224,.30)}
.cut-lb-rank{font-size:12px;font-weight:700;color:${VIOLET};letter-spacing:.02em}
.cut-lb-name{font-size:13px;color:#fff;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cut-lb-you{color:${VIOLET};font-weight:500;font-size:11px;letter-spacing:.04em}
.cut-lb-bar{height:5px;background:#232323;border-radius:999px;overflow:hidden}
.cut-lb-bar-fill{height:100%;background:${VIOLET};transition:width .6s cubic-bezier(.2,.8,.2,1)}
.cut-lb-total{font-size:13px;font-weight:600;color:${VIOLET};font-variant-numeric:tabular-nums;min-width:32px;text-align:right}

.cut-share-grid{display:grid;grid-template-columns:1fr;gap:10px}
@media(min-width:640px){.cut-share-grid{grid-template-columns:repeat(3,1fr)}}
.cut-share-btn{background:#141414;border:.5px solid #232323;color:#e8e8e8;padding:12px 14px;border-radius:12px;font-size:12px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:inherit;text-decoration:none;transition:border-color .18s,background .18s}
.cut-share-btn:hover{border-color:${VIOLET};background:#181818}

.cut-winner-ribbon{position:relative;background:linear-gradient(90deg,${VIOLET} 0%,#B5AEF5 50%,${VIOLET} 100%);color:${DARK};padding:28px 24px;overflow:hidden;box-shadow:inset 0 -1px 0 rgba(0,0,0,.18),0 8px 32px -12px rgba(91,79,224,.55);animation:cut-ribbon-in .6s cubic-bezier(.2,.8,.2,1)}
.cut-winner-stripes{position:absolute;inset:0;background:repeating-linear-gradient(45deg,transparent 0 14px,rgba(0,0,0,.05) 14px 24px);opacity:.9;pointer-events:none}
.cut-winner-inner{max-width:1120px;margin:0 auto;display:flex;align-items:center;gap:22px;position:relative;z-index:1}
.cut-winner-trophy{font-size:40px;line-height:1;animation:cut-trophy-tilt 3.5s ease-in-out infinite}
.cut-winner-eyebrow{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;opacity:.7}
.cut-winner-title{font-size:24px;font-weight:700;letter-spacing:-.01em;margin-top:2px}
.cut-winner-sub{font-size:13px;opacity:.75;margin-top:3px}
@keyframes cut-ribbon-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
@keyframes cut-trophy-tilt{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(6deg)}}
`;
